import { Component, inject, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { AppInsightsService } from '@k12/error_handler';
import { FormInlineErrorComponent, State } from '@k12/forms';
import { EmployeeSummary, ProviderType, SeverityLevel, SubService, ToastMessageType } from '@k12/models';
import { ToastMessageService } from '@k12/notifications';
import { IUserRecordEmployeeService, USER_RECORD_EMPLOYEE_SERVICE } from '@k12/tokens';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-license-info',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    FormInlineErrorComponent
  ],
  templateUrl: './license-info.component.html'
})
export class LicenseInfoComponent implements OnInit {
  @Input() formGroup!: FormGroup;
  @Input() providerType: string = '';
  @Input() states: State[] = [];
  @Input() subServices: SubService[] = [];
  @Input() showReviewStatus: boolean = false;
  @Input() userId: string = '';

  employeeCount: number = 0;
  filteredIssuingStates!: Observable<State[]>;

  public readonly ProviderType = ProviderType;

  private readonly appInsightsService = inject(AppInsightsService);
  private readonly employeeService = inject<IUserRecordEmployeeService>(USER_RECORD_EMPLOYEE_SERVICE, {
    optional: true
  });
  private readonly toastMessageService = inject(ToastMessageService);

  public readonly npiNumber2ErrorMessages: Record<string, string | ((value: unknown) => string)> = {
    required: 'NPI 2 Number is required for facilities with 2 or more employees.',
    tenDigitNumberValidator: 'Please enter a valid 10-digit number'
  };

  ngOnInit(): void {
    this.filteredIssuingStates = this.formGroup.get('issuingState')!.valueChanges.pipe(
      startWith(this.formGroup.get('issuingState')!.value),
      map((value: string | State) => this.filterStates(value))
    );

    if (this.isProviderType(ProviderType.Therapy)) {
      this.getEmployeeCount();
    }
  }

  isProviderType(type: ProviderType): boolean {
    return this.normalizeProviderType(this.providerType) === type;
  }

  private normalizeProviderType(value: string | null | undefined): string {
    return (value ?? '').trim().toLowerCase();
  }

  private filterStates(value: string | State): State[] {
    const filterValue = typeof value === 'string' ? value.toLowerCase() : value?.name?.toLowerCase() || '';
    return this.states.filter((state) => state.name.toLowerCase().includes(filterValue));
  }

  displayStateName(state: State | null): string {
    return state?.name || '';
  }

  private getEmployeeCount(): void {
    if (this.employeeService && this.userId) {
      this.employeeService.GetAllEmployeesSummaryAdmin(this.userId).subscribe({
        next: (res: EmployeeSummary[]) => {
          if (res.length >= 2) {
            this.formGroup.get('npiNumber')?.disable();
            this.formGroup.get('npiNumber2')?.enable();
          }
          if (res.length < 2) {
            this.formGroup.get('npiNumber')?.enable();
            this.formGroup.get('npiNumber2')?.disable();
          }
        },
        error: (error: unknown) => {
          const err = error instanceof Error ? error : new Error(String(error));
          this.appInsightsService.log(err, SeverityLevel.error);
          this.toastMessageService.showToast(ToastMessageType.Error, err.message);
        }
      });
    }
  }
}